import type { StateCreator } from 'zustand';

export interface SelectedElement {
  tagName: string;
  text: string;
  classes: string;
  rect: DOMRect;
  loc?: string;
  /** Explicit JSON field path from data-content-path attribute, e.g. "home.json:nav.links.1.label" */
  contentPath?: string;
}

export interface WebsiteBuilderSlice {
  wbPanelOpen: boolean;
  wbSiteId: string | null;
  wbActiveTab: 'preview' | 'code' | 'settings';
  wbDeviceMode: 'desktop' | 'tablet' | 'mobile';
  wbSelectedFile: string | null;
  wbPreviewKey: number;
  /** True when the next new conversation should be scoped to the website builder agent. */
  wbConvScoped: boolean;
  /** Maps conversationId → siteId for conversations where the WB panel has been opened. */
  wbConvSiteMap: Record<string, string>;
  /** Live build progress steps received from the agent via SSE. */
  wbBuildSteps: string[];
  /** Currently selected element in the preview iframe. */
  wbSelectedElement: SelectedElement | null;
  /** True while the agent SSE stream is active — visual editing is locked. */
  wbAgentStreaming: boolean;
  /** True when the user has activated "Edit mode" in the preview toolbar. */
  wbEditMode: boolean;

  openWbPanel: (siteId: string | null) => void;
  closeWbPanel: () => void;
  setWbTab: (tab: WebsiteBuilderSlice['wbActiveTab']) => void;
  setWbDeviceMode: (mode: WebsiteBuilderSlice['wbDeviceMode']) => void;
  setWbSelectedFile: (path: string | null) => void;
  bumpWbPreviewKey: () => void;
  setWbConvScoped: (scoped: boolean) => void;
  setWbConvSite: (conversationId: string, siteId: string) => void;
  addWbBuildStep: (label: string) => void;
  clearWbBuildSteps: () => void;
  setWbSelectedElement: (el: SelectedElement | null) => void;
  setWbAgentStreaming: (streaming: boolean) => void;
  setWbEditMode: (on: boolean) => void;
}

export const createWebsiteBuilderSlice: StateCreator<WebsiteBuilderSlice> = (set) => ({
  wbPanelOpen: false,
  wbSiteId: null,
  wbActiveTab: 'preview',
  wbDeviceMode: 'desktop',
  wbSelectedFile: null,
  wbPreviewKey: 0,
  wbConvScoped: false,
  wbConvSiteMap: {},
  wbBuildSteps: [],
  wbSelectedElement: null,
  wbAgentStreaming: false,
  wbEditMode: false,

  openWbPanel: (siteId) => set({ wbPanelOpen: true, wbSiteId: siteId, wbBuildSteps: [] }),
  closeWbPanel: () =>
    set({ wbPanelOpen: false, wbSiteId: null, wbSelectedElement: null, wbEditMode: false }),
  setWbTab: (tab) => set({ wbActiveTab: tab }),
  setWbDeviceMode: (mode) => set({ wbDeviceMode: mode }),
  setWbSelectedFile: (path) => set({ wbSelectedFile: path }),
  bumpWbPreviewKey: () => set((s) => ({ wbPreviewKey: s.wbPreviewKey + 1 })),
  setWbConvScoped: (scoped) => set({ wbConvScoped: scoped }),
  setWbConvSite: (conversationId, siteId) =>
    set((s) => ({ wbConvSiteMap: { ...s.wbConvSiteMap, [conversationId]: siteId } })),
  addWbBuildStep: (label) => set((s) => ({ wbBuildSteps: [...s.wbBuildSteps, label] })),
  clearWbBuildSteps: () => set({ wbBuildSteps: [] }),
  setWbSelectedElement: (el) => set({ wbSelectedElement: el }),
  setWbAgentStreaming: (streaming) => set({ wbAgentStreaming: streaming }),
  setWbEditMode: (on) =>
    set((s) => ({ wbEditMode: on, wbSelectedElement: on ? s.wbSelectedElement : null }))
});
